import React, { useEffect } from "react";
import { getUrl } from "@helpers/switchLanguage";

import { initialCallPaths } from "@helpers/initial-call";
import CategoryTemplate from "@components/category/categoryTemplate";
import OverView from "@components/category/overView";
import SideWidget from "@components/shared/sideWidget";
import initialCall from "@helpers/initial-call";
import {
    GQLCategories,
    GQLDomains,
    GQLTextBlocks,
    GQLUrls,
} from "@gql/types/gql";
import { GetServerSidePropsContext } from "next";
import { useQuery } from "@tanstack/react-query";
import MetaTags from "@components/shared/metaTags";
import {
    getCategoriesDescription,
    getCategoriesKeyWords,
    resolveURL,
} from "@lib/sharedUtils";
import { useRouter } from "next/router";
import {
    ICategoryByIdPageProps,
    ICustomDomain,
    Languages,
    TranslationsObject,
} from "@genericTypes/sharedTypes";
import { translate } from "@helpers/translationObjectFormatter";
import { titleDecoder } from "@lib/sharedUtils";
import { Session } from "pages/api/session";
import { activePageAtom } from "@store/generalStore";
import { useSetAtom } from "jotai";

const Pg = ({ title }: { title: string }) => {
    const { query } = useRouter();
    const setActivePage = useSetAtom(activePageAtom);
    const id = query.id as string;
    const { data: session } = useQuery<Session>(["session"]);
    const { data: props } = useQuery<ICategoryByIdPageProps>([
        "categoryPageProps",
    ]);
    const { data: translations } = useQuery<TranslationsObject>([
        "translations",
    ]);

    useEffect(() => {
        setActivePage("categories");
    }, []);
    const {
        category,
        offers,
        topics,
        recentPosts,
        offersListTextBlock: textBlock,
    } = props ?? {};

    const bestOfferSideWidget: { title: string; link: string }[] | undefined =
        offers?.slice(0, 10)?.map((offer) => ({
            title: offer?.name ?? offer?.summaryTitle ?? offer?.title ?? "",
            link: `#${offer?.slug as string}`,
        }));

    const myList = [
        {
            title: translate(translations, "several_general_contents"),
            slug: "contents",
            content: [
                {
                    title: `${translate(
                        translations,
                        "several_category_sideWidget_about",
                    )} ${
                        category?.label ??
                        category?.name ??
                        (category?.title as string)
                    }`,
                    link: "#about",
                },
                {
                    title: `${translate(
                        translations,
                        "several_category_sideWidget_description",
                    )}`,
                    link: "#description",
                },

                {
                    title: textBlock
                        ? titleDecoder(
                              (textBlock as GQLTextBlocks)?.text as string,
                              {
                                  category: (category?.name as string) ?? "",
                                  region: session?.visitDetails?.region,
                              },
                          )
                        : `${translate(
                              translations,
                              "several_category_sideWidget_bestInCategory",
                          )} ${
                              category?.label ??
                              category?.name ??
                              (category?.title as string)
                          }`,
                    link: "#best",
                },
                ...((bestOfferSideWidget as {
                    title: string;
                    link: string;
                }[]) ?? []),
            ],
        },

        {
            title: translate(translations, "several_general_recentPosts"),
            slug: "recent posts",
            content: topics?.length
                ? topics
                : (recentPosts as { title: string; link: string }[]),
        },
    ];
    const { data: domainInfo } = useQuery<ICustomDomain, Error>({
        queryKey: ["domain"],
    });

    return (
        <>
            <MetaTags
                title={`${
                    title
                        ? title
                        : (category?.label as string)
                        ? (category?.label as string)
                        : (category?.name as string)
                } | Several.com`}
                image={
                    (category?.mainImage?.fullPath as string) ??
                    `${getUrl(
                        process.env.NEXT_PUBLIC_DEFAULT_LANG as Languages,
                        "/placeholder_several.jpg",
                    )}`
                }
                description={
                    getCategoriesDescription(
                        category as GQLCategories,
                        domainInfo?.domain as GQLDomains,
                        translate(
                            translations,
                            "several_general_meta_description",
                        ),
                    ) as string
                }
                keyWords={
                    getCategoriesKeyWords(
                        category as GQLCategories,
                        domainInfo?.domain as GQLDomains,
                    ) as string
                }
                url={`${resolveURL(category?.url as GQLUrls)}`}
            />
            <CategoryTemplate>
                <section className=" flex flex-col-reverse lg:flex-row lg:items-start items-center lg:my-16 mb-16">
                    <SideWidget
                        myList={myList}
                        contentToBeSharedUrl={`${resolveURL(
                            category?.url as GQLUrls,
                        )}`}
                        category={category as GQLCategories}
                    />
                    <OverView
                        category={category as GQLCategories}
                        categoryId={parseInt(id, 10)}
                        listOfContentsMobile={myList[0].content}
                        recentPostsMobile={myList[1].content}
                    />
                </section>
            </CategoryTemplate>
        </>
    );
};

export function getStaticPaths() {
    return initialCallPaths();
}
export async function getStaticProps(ctx: GetServerSidePropsContext) {
    return await initialCall("CategoryId", ctx);
}

export default Pg;
